import React, { useState, useMemo } from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import { formatCurrency } from '../../../helpers/UtilityHelper'
import { numberPrecision } from '../../../common/util'
import palette from '../../../../theme/palette'

export default function MerchantBreakdownTable({ data = [], columns = [], title, loading, defaultSort }) {
  /*
    columns shape: [
      { key: 'merchantName', label: 'Merchant', format: 'text' },
      { key: 'totalCalls', label: 'Calls', format: 'number', align: 'right' },
    ]
    format options: text, number, currency, percent
  */

  const [orderBy, setOrderBy] = useState(defaultSort?.field || columns[0]?.key)
  const [order, setOrder] = useState(defaultSort?.direction || 'desc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const sorted = useMemo(() => {
    const arr = [...data]
    arr.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return arr
  }, [data, orderBy, order])

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const formatCell = (val, fmt) => {
    if (val == null) return '—'
    switch (fmt) {
      case 'number': return numberPrecision(val)
      case 'currency': return formatCurrency(val)
      case 'percent': return `${Number(val).toFixed(1)}%`
      default: return val
    }
  }

  if (loading) {
    return (
      <Paper sx={{ p: 2.5 }}>
        {title && <Skeleton width={180} height={22} sx={{ mb: 2 }} />}
        {[...Array(5)].map((_, i) => <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />)}
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      {title && <Typography variant='subtitle1' sx={{ mb: 1.5 }}>{title}</Typography>}
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.key} align={col.align || 'left'} sx={{ bgcolor: palette.grey[200] }}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === col.key && order === 'asc'
                      setOrder(isAsc ? 'desc' : 'asc')
                      setOrderBy(col.key)
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row, idx) => (
              <TableRow key={row.merchantOid || idx} hover>
                {columns.map(col => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    {formatCell(row[col.key], col.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!paged.length && (
              <TableRow>
                <TableCell colSpan={columns.length} align='center' sx={{ py: 4, color: 'text.disabled' }}>
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length > 10 && (
        <TablePagination
          component='div'
          count={data.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </Paper>
  )
}
